'use memo';

import { useEffect, useState } from 'react';
import { useSelectedCampaign, useSelectedContract } from 'hooks';
import { MenuTitles, Pages } from 'constants/menu';
import { ContractSubPage } from '../ContractSubPage';
import TerminateContractAction from '../TerminateContractAction';
import { ErrorModal } from 'helpers/ErrorModal';
import { isDirtyEqual } from 'utils/compares';
import { getContractInformationSubPages } from './ContractInformationSubPages';
import { SubPagePanel } from 'components/panel/SubPagePanel';
import {
  FormCondition,
  Condition,
  ConditionType,
  TimeUnit,
  SignboardRemoval,
  SignboardRemovalRemovalType,
  StaffUpdateContractSettlementRequest,
  useStaffGetContractDates,
  useInvalidateUseStaffGetContractSettlement,
  useStaffGetContractSettlement,
  useStaffUpdateContractSettlement,
} from '@urbanx/agx.campaigns.client';
import {
  AgxBadge,
  AgxButton,
  AgxCollapsible,
  AgxDatePicker,
  AgxRadioGroup,
  AgxDivider,
  AgxSpacer,
  AgxTextInput,
  AgxTypography,
} from '@urbanx/agx.ui-components2';
import dayjs from 'dayjs';
import LoadingState from 'components/loadingState/LoadingState';
import ConditionDate from './components/ConditionDate';
import { useFormConditionDueDate } from './components/useFormConditionDueDate';
import { RexDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { isEqual } from 'lodash-es';
import { useGetSpecialConditionsInfo } from 'hooks/useGetSpecialConditionsInfo';
import useRexDisconnectedModal from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal';
import slugify from 'slugify';
import { dateOnlyToDate, toDateOnlyString } from '@urbanx/agx.ui-components2';

const Settlement = () => {
  const { contract, campaignId } = useSelectedContract();
  const { campaign } = useSelectedCampaign();
  const [isLoaded, setIsLoaded] = useState(false);
  const [validate, setValidate] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [submitFormErrors, setSubmitFormErrors] = useState<string[]>([]);
  const [isEditingSettlementDate, setIsEditingSettlementDate] = useState(false);
  const invalidateUseStaffGetContractSettlement = useInvalidateUseStaffGetContractSettlement();

  const { specialConditionIdToClauseNumberMap } = useGetSpecialConditionsInfo(
    campaignId,
    contract?.contractId
  );

  const {
    data: contractSettlement,
    isPending,
    isError,
  } = useStaffGetContractSettlement(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  );

  const { data: contractDates } = useStaffGetContractDates(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  );

  const [settlementInfo, setSettlementInfo] = useState<FormCondition | undefined>(
    contractSettlement?.settlementInfo
  );

  const [signboardRemoval, setSignboardRemoval] = useState<SignboardRemoval | undefined>(
    contractSettlement?.signboardRemoval
  );

  const { modal: rexDisconnectedModal, openWithChanges } = useRexDisconnectedModal();

  useEffect(() => {
    setSettlementInfo(contractSettlement?.settlementInfo);
    setSignboardRemoval(contractSettlement?.signboardRemoval);
    setIsLoaded(!isPending);
  }, [contractSettlement, isPending]);

  useEffect(() => {
    setValidate(
      !isDirtyEqual(settlementInfo, contractSettlement?.settlementInfo) ||
        !isDirtyEqual(signboardRemoval, contractSettlement?.signboardRemoval)
    );
  }, [settlementInfo, signboardRemoval]);

  const { staffUpdateContractSettlement, staffUpdateContractSettlementPending } =
    useStaffUpdateContractSettlement({
      onSuccess: () => {},
      onError: () => {},
    });

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaignId || '',
    campaignId !== undefined
  );

  const isFormDirty =
    !isDirtyEqual(settlementInfo, contractSettlement?.settlementInfo) ||
    !isDirtyEqual(signboardRemoval, contractSettlement?.signboardRemoval);

  const prevSettlementDueDate = useFormConditionDueDate(
    contractSettlement?.settlementInfo,
    contractDates?.contractDate
  );
  const currSettlementDueDate = useFormConditionDueDate(
    settlementInfo,
    contractDates?.contractDate
  );
  const [editedSettlementDate, setEditedSettlementDate] = useState<Date | undefined>(
    currSettlementDueDate?.dueDate ?? undefined
  );

  useEffect(() => {
    setEditedSettlementDate(dateOnlyToDate(currSettlementDueDate.dueDateString) ?? undefined);
  }, [currSettlementDueDate.dueDateString]);

  const settlementBadgeText = (() => {
    if (!settlementInfo) return '';

    if (
      settlementInfo.condition === Condition.NdaysFromDate &&
      settlementInfo.conditionType === ConditionType.Contract &&
      settlementInfo.daysFromDate &&
      settlementInfo.daysFromDate > 0 &&
      (settlementInfo.fromDateTimeUnit === TimeUnit.Days ||
        settlementInfo.fromDateTimeUnit === TimeUnit.Weeks)
    ) {
      return `${settlementInfo.daysFromDate} ${settlementInfo.fromDateTimeUnit.toLowerCase()} from contract date`;
    }

    return currSettlementDueDate.dueDateString
      ? dayjs(currSettlementDueDate.dueDateString).format('Do MMM')
      : '';
  })();

  const onEditSettlementDate = () => {
    if (!settlementInfo) return;

    if (isEditingSettlementDate) {
      if (!editedSettlementDate) return;

      setSettlementInfo({
        ...settlementInfo,
        condition: Condition.OnSpecificDate,
        referenceDate: toDateOnlyString(editedSettlementDate),
        daysFromDate: undefined,
        conditionType: undefined,
        specialConditionId: undefined,
      });
      setIsEditingSettlementDate(false);
      return;
    }

    setIsEditingSettlementDate(true);
  };

  const onSave = async (success: () => void) => {
    if (!contract?.contractId) return;

    if (!rexListing && isPendingRexListing === false) {
      openWithChanges(
        !isEqual(prevSettlementDueDate.dueDateString, currSettlementDueDate.dueDateString)
          ? [
              {
                field: RexDisconnectedModalFields.SettlementDate,
                value: currSettlementDueDate.dueDateString,
              },
            ]
          : []
      );
    }

    const request: StaffUpdateContractSettlementRequest = {
      settlementInfo: settlementInfo,
      signboardRemoval: signboardRemoval,
    };

    await staffUpdateContractSettlement(
      {
        campaignId: campaignId ?? '',
        contractId: contract.contractId,
        body: request,
      },
      {
        onSuccess: async (data) => {
          if (data?.success) {
            success();
            await invalidateUseStaffGetContractSettlement(campaignId ?? '', contract.contractId);
          } else {
            setSubmitFormErrors(data?.errors ?? []);
            setOpenErrorModal(true);
          }
        },
      }
    );
  };

  if (!isLoaded) {
    return <LoadingState />;
  }

  return (
    <ContractSubPage
      key={`SettlementPage${isPending ? 'Loaded' : ''}`}
      pageTitle="Contract Information"
      secondaryActiveMenu={MenuTitles.CONTRACT_INFORMATION}
      currentPage={Pages.SETTLEMENT}
      subPages={getContractInformationSubPages({ campaignState: campaign?.state })}
      onSaveChanges={onSave}
      isFormDirty={isFormDirty}
      isSaving={staffUpdateContractSettlementPending}
      isError={isError}
      secondaryActions={<TerminateContractAction />}
    >
      {settlementInfo && (
        <AgxCollapsible
          key={`SettlementPanel${isPending ? 'Loaded' : ''}`}
          variant="withDivider"
          enableToggle
          open
          customTitle={
            <AgxSpacer fill direction="horizontal" gap="xs" contentAlignment="center">
              <AgxTypography
                id={`condition-${slugify('Settlement')}`}
                type="title"
                size="large"
                color="var(--text-primary)"
              >
                Settlement
              </AgxTypography>
              {settlementBadgeText && (
                <AgxBadge type="colourfill" colour="yellow" size="md" text={settlementBadgeText} />
              )}
            </AgxSpacer>
          }
          content={
            <>
              <AgxSpacer
                direction="horizontal"
                gap="spacing-md"
                fill
                padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
              >
                <ConditionDate
                  id="settlementInfo"
                  condition={settlementInfo}
                  specialConditionIdToClauseNumberMap={specialConditionIdToClauseNumberMap ?? {}}
                />
              </AgxSpacer>
              <AgxDivider variant="horizontal" />
              <AgxSpacer
                direction="horizontal"
                gap="spacing-md"
                fill
                padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
                contentAlignment="end"
              >
                <AgxDatePicker
                  id="settlementInfo-due-date"
                  stretch
                  disabled={!isEditingSettlementDate}
                  optionalText=""
                  label="Due Date"
                  value={
                    editedSettlementDate ? dayjs(editedSettlementDate).format('YYYY-MM-DD') : ''
                  }
                  onChange={(value) => {
                    setEditedSettlementDate(value ? new Date(value) : undefined);
                  }}
                />
                <AgxButton
                  id="settlementInfo-edit-due-date"
                  variant="hollow"
                  text={isEditingSettlementDate ? 'Save' : 'Edit'}
                  onClick={onEditSettlementDate}
                />
              </AgxSpacer>
            </>
          }
        />
      )}
      <SubPagePanel
        key={`SignboardRemovalPanel${isPending ? 'Loaded' : ''}`}
        panelTitle="Signboard Removal"
        expandable
        defaultExpanded
      >
        <AgxRadioGroup
          id="signboardRemoval_RemovalType"
          key={`signboardRemoval_RemovalType${isPending ? 'Loaded' : ''}`}
          layout="stacked"
          value={signboardRemoval?.removalType || ''}
          onChange={(val) => {
            if (val === signboardRemoval?.removalType) return;
            setSignboardRemoval((prev) => ({
              ...prev,
              removalType: val as SignboardRemovalRemovalType,
            }));
          }}
          options={[
            {
              label: 'Upon settlement',
              value: SignboardRemovalRemovalType.UponSettlement,
            },
            {
              label: 'On Specific Date',
              value: SignboardRemovalRemovalType.OnSpecificDate,
            },
            {
              label: 'No signboard installed',
              value: SignboardRemovalRemovalType.NoSignboardInstalled,
            },
          ]}
          subContent={
            signboardRemoval?.removalType === SignboardRemovalRemovalType.OnSpecificDate
              ? [
                  <AgxSpacer
                    direction="vertical"
                    key={`signboardRemoval_specificDate_Column${isPending ? 'Loaded' : ''}`}
                    gap="spacing-md"
                    padding={{
                      top: 'spacing-xl',
                      bottom: 'spacing-xl',
                      left: 'spacing-xl',
                      right: 'spacing-xl',
                    }}
                  >
                    <AgxTextInput
                      id="signboardRemoval_specificDate"
                      key={`signboardRemoval_specificDate${isPending ? 'Loaded' : ''}`}
                      onInputValueChange={({ value }) => {
                        setSignboardRemoval((prev) => ({
                          ...prev,
                          specificDate: value || undefined,
                        }));
                      }}
                      required
                      hideOptionalText
                      type="date"
                      defaultValue={
                        signboardRemoval?.specificDate
                          ? dayjs(signboardRemoval?.specificDate).format('YYYY-MM-DD')
                          : undefined
                      }
                      errorText={
                        validate && !signboardRemoval?.specificDate ? 'Enter a date' : undefined
                      }
                    />
                  </AgxSpacer>,
                ]
              : undefined
          }
        />
      </SubPagePanel>
      {openErrorModal && (
        <ErrorModal errorList={submitFormErrors} setShowModal={setOpenErrorModal} />
      )}
      {rexDisconnectedModal}
    </ContractSubPage>
  );
};

export default Settlement;
